/**
 * Observability Service - NeoApp
 * Serviço centralizado para telemetria, captura de erros e logs de performance.
 */

import { db } from "./firebase"
import { collection, addDoc, Timestamp } from "firebase/firestore"

// ── Constantes de Log (antes em models/log, agora inline) ──
export const LOG_TYPE = {
    ERROR: 'ERROR',
    PERFORMANCE: 'PERFORMANCE',
    AUDIT: 'AUDIT',
};

export const LOG_SEVERITY = {
    DEBUG: 'DEBUG',
    INFO: 'INFO',
    WARNING: 'WARNING',
    ERROR: 'ERROR',
    CRITICAL: 'CRITICAL',
};

/**
 * Cria o payload de log para gravar no Firestore.
 */
const buildLogEntry = ({ type, severity, message, metadata = {} }) => ({
    type,
    severity,
    message,
    metadata,
    timestamp: Timestamp.now(),
    createdAt: Timestamp.now(),
});

class ObservabilityService {
    constructor() {
        this.isInitialized = true
        this.logsCollection = collection(db, "system_logs")
    }

    /**
     * Registra um erro no sistema e opcionalmente em serviços externos (Sentry/LogRocket)
     */
    async logError(error, context = {}) {
        console.error("[NeoApp Observability]", error, context)

        try {
            const entry = buildLogEntry({
                type: LOG_TYPE.ERROR,
                severity: context.severity || LOG_SEVERITY.ERROR,
                message: error.message || String(error),
                metadata: {
                    stack: error.stack,
                    ...context,
                },
            })

            await addDoc(this.logsCollection, entry)
        } catch (e) {
            console.error("Falha ao reportar erro para o Firestore:", e)
        }
    }

    /**
     * Registra eventos de performance (ex: tempo de carregamento de páginas pesadas)
     */
    async logPerformance(metricName, value, metadata = {}) {

        try {
            const entry = buildLogEntry({
                type: LOG_TYPE.PERFORMANCE,
                severity: LOG_SEVERITY.INFO,
                message: `Metric: ${metricName}`,
                metadata: {
                    metricName,
                    value,
                    ...metadata,
                },
            })

            await addDoc(this.logsCollection, entry)
        } catch (e) {
            // Ignoramos erros de log de performance para não impactar o usuário
        }
    }

    /**
     * Log de auditoria para ações importantes de negócio
     */
    async logAudit(action, result, metadata = {}) {
        try {
            const entry = buildLogEntry({
                type: LOG_TYPE.AUDIT,
                severity: LOG_SEVERITY.INFO,
                message: `Action: ${action} - Result: ${result}`,
                metadata: {
                    action,
                    result,
                    ...metadata,
                },
            })
            await addDoc(this.logsCollection, entry)
        } catch (e) {
            console.error("Falha ao registrar auditoria:", e)
        }
    }
}

const observabilityService = new ObservabilityService()
export default observabilityService
